import {
  type AuctionListing,
  type AuctionListRequest,
  AuctionListingResponseSchema,
  AuctionMineResponseSchema,
  type AuctionSearchQuery,
  type AuctionSearchResponse,
  AuctionSearchResponseSchema,
} from '@shefira/shared';

import { call } from './account.js';

/**
 * The auction house's endpoints (ADR 0035). Everything goes over HTTP with
 * the character token: a lot lives for hours, outside any room. The server
 * escrows the stack, moves the gold and pushes the bag back through the
 * room; this module only asks. A refusal arrives as an `AuthError` whose
 * `code` is the server's reason and whose message is already in French.
 */

/** Search the active lots; every field is optional and the server fills the defaults. */
export async function searchListings(
  token: string,
  ticket: string,
  query: Partial<AuctionSearchQuery>,
): Promise<AuctionSearchResponse> {
  const params = new URLSearchParams();
  const q = query.q?.trim() ?? '';
  if (q !== '') params.set('q', q);
  if (query.slot !== undefined) params.set('slot', query.slot);
  if (query.kind !== undefined) params.set('kind', query.kind);
  if (query.sort !== undefined) params.set('sort', query.sort);
  if (query.page !== undefined) params.set('page', String(query.page));
  const search = params.toString();
  return call(
    `/auction/listings${search === '' ? '' : `?${search}`}`,
    AuctionSearchResponseSchema,
    { method: 'GET', token, headers: { 'x-auction-access': ticket } },
  );
}

/** Put a stack up for sale: it leaves the bag, the house keeps it until sold or collected. */
export async function listItem(
  token: string,
  ticket: string,
  request: AuctionListRequest,
): Promise<AuctionListing> {
  const response = await call('/auction/listings', AuctionListingResponseSchema, {
    method: 'POST',
    token,
    headers: { 'x-auction-access': ticket },
    body: request,
  });
  return response.listing;
}

async function act(
  token: string,
  ticket: string,
  id: string,
  verb: string,
): Promise<AuctionListing> {
  const response = await call(
    `/auction/listings/${encodeURIComponent(id)}/${verb}`,
    AuctionListingResponseSchema,
    { method: 'POST', token, headers: { 'x-auction-access': ticket } },
  );
  return response.listing;
}

export async function buyListing(
  token: string,
  ticket: string,
  id: string,
): Promise<AuctionListing> {
  return act(token, ticket, id, 'buy');
}

/** Withdraw an active lot: it comes back to the bag, or waits to be collected if the bag is full. */
export async function cancelListing(
  token: string,
  ticket: string,
  id: string,
): Promise<AuctionListing> {
  return act(token, ticket, id, 'cancel');
}

/** Take back an expired or withdrawn lot that is waiting at the house. */
export async function collectListing(
  token: string,
  ticket: string,
  id: string,
): Promise<AuctionListing> {
  return act(token, ticket, id, 'collect');
}

/** My active lots and those waiting to be collected. */
export async function myListings(token: string, ticket: string): Promise<AuctionListing[]> {
  const response = await call('/auction/mine', AuctionMineResponseSchema, {
    method: 'GET',
    token,
    headers: { 'x-auction-access': ticket },
  });
  return response.listings;
}
